export type HomeRouletteState =
  | { phase: "idle"; highlighted: null; selected: null }
  | { phase: "spinning"; highlighted: number; selected: null }
  | { phase: "result"; highlighted: null; selected: number };

// Orbit duration/easing follows the observed production interaction (25 Oct 2026).
// Intermediate highlight cadence remains a visual tuning choice.
export const HOME_ROULETTE_MOTION = {
  highlightCount: 10,
  highlightMs: 280,
  finalPauseMs: 480,
  durationMs: 3000,
} as const;

// Clockwise degrees from twelve o'clock; same petal mapping as production.
const HOME_ORBIT_TARGETS = [317, 45, 92, 146, 212, 268] as const;
export function nextHomeOrbitAngle(
  current: number,
  selected: number,
  reducedMotion: boolean,
): number {
  const target = HOME_ORBIT_TARGETS[selected];
  if (target === undefined) throw new Error("Invalid orbit direction");
  const normalized = ((current % 360) + 360) % 360;
  return (
    current + (reducedMotion ? 0 : 720) + ((target - normalized + 360) % 360)
  );
}

/** Same cubic-bezier as the CSS orbit: highlights follow position, not a second timer. */
export function homeOrbitProgress(elapsedMs: number): number {
  const x = Math.max(
    0,
    Math.min(1, elapsedMs / HOME_ROULETTE_MOTION.durationMs),
  );
  if (x === 0 || x === 1) return x;
  const bezier = (t: number, a: number, b: number) =>
    3 * (1 - t) ** 2 * t * a + 3 * (1 - t) * t ** 2 * b + t ** 3;
  let low = 0;
  let high = 1;
  for (let i = 0; i < 18; i += 1) {
    const t = (low + high) / 2;
    if (bezier(t, 0.34, 0.16) < x) low = t;
    else high = t;
  }
  return bezier((low + high) / 2, 0.08, 1);
}

export function homePetalAtOrbitAngle(angle: number): number {
  const normalized = ((angle % 360) + 360) % 360;
  return HOME_ORBIT_TARGETS.reduce<number>((nearest, target, index) => {
    const distance = (value: number) =>
      Math.abs(((value - normalized + 540) % 360) - 180);
    return distance(target) < distance(HOME_ORBIT_TARGETS[nearest])
      ? index
      : nearest;
  }, 0);
}

const IDLE: HomeRouletteState = {
  phase: "idle",
  highlighted: null,
  selected: null,
};

/** One cancellable selection cycle, shared by the desktop petals and mobile grid.
 * Navigation belongs to the explicit direction action in the view, never a timer.
 */
export function createHomeRoulette(
  directionCount: number,
  onChange: (state: HomeRouletteState) => void,
  random: () => number = Math.random,
) {
  if (!Number.isInteger(directionCount) || directionCount < 1) {
    throw new Error("Roulette requires at least one direction");
  }

  let state: HomeRouletteState = IDLE;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let generation = 0;
  let disposed = false;
  let destination: number | null = null;

  const publish = (next: HomeRouletteState) => {
    state = next;
    onChange(next);
  };
  const clearTimer = () => {
    generation += 1;
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  };
  const pick = () =>
    Math.min(
      directionCount - 1,
      Math.max(0, Math.floor(random() * directionCount)),
    );

  return {
    getState: () => state,
    getDestination: () => destination,
    start(reducedMotion: boolean): boolean {
      if (disposed || state.phase === "spinning") return false;
      clearTimer();
      const currentGeneration = generation;
      // Pick the destination once; visual highlights cannot change the result.
      const selected = pick();
      destination = selected;
      if (reducedMotion) {
        publish({ phase: "result", highlighted: null, selected });
        return true;
      }

      let step = 0;
      const advance = () => {
        if (disposed || generation !== currentGeneration) return;
        if (step === HOME_ROULETTE_MOTION.highlightCount) {
          timer = undefined;
          publish({ phase: "result", highlighted: null, selected });
          return;
        }
        let highlighted = pick();
        // Adjacent ticks should be visibly distinct, including with repeated RNG values.
        if (
          state.phase === "spinning" &&
          highlighted === state.highlighted &&
          directionCount > 1
        ) {
          highlighted = (highlighted + 1) % directionCount;
        }
        step += 1;
        timer = setTimeout(
          advance,
          step === HOME_ROULETTE_MOTION.highlightCount
            ? HOME_ROULETTE_MOTION.finalPauseMs
            : HOME_ROULETTE_MOTION.highlightMs,
        );
        publish({ phase: "spinning", highlighted, selected: null });
      };
      advance();
      return true;
    },
    finishWithoutMotion() {
      if (disposed || state.phase !== "spinning") return;
      // A preference change skips the remaining highlights without a redraw.
      clearTimer();
      if (destination !== null)
        publish({ phase: "result", highlighted: null, selected: destination });
    },
    cancel() {
      if (disposed) return;
      clearTimer();
      destination = null;
      publish(IDLE);
    },
    dispose() {
      clearTimer();
      disposed = true;
    },
  };
}
